/**
 * Turning an uploaded CSV into the rows a General Process runs on.
 *
 * Shared by the two places a file is picked: the Source tab (design time —
 * a SAMPLE, to declare the columns) and Review & Execute (run time — the
 * real file). Both must apply the SAME header→column mapping, or a process
 * would preview differently from how it runs.
 *
 * Parsing stays in the browser: the user is about to review these rows
 * anyway, so a multipart upload would add a round-trip and a stored copy of
 * the file for no gain. The server still re-checks and coerces everything
 * (`genprocess/file_source.py`) — the mapping applied here is a convenience,
 * never a trust boundary.
 */

import { parseFile, type ParsedFile } from '@/assistant/parseFile'
import type { FileSource, FileSourceColumn } from './types'

/** Above this the grid stops being reviewable; still allowed, with a warning. */
export const IMPORT_WARN_ROWS = 50_000
/** Beyond this the payload stops being a sane JSON request — refused. */
export const IMPORT_MAX_ROWS = 200_000

export interface ImportResult {
  filename: string
  headers: string[]
  /** Rows keyed by DECLARED column name, ready to send as `input_rows`. */
  rows: Record<string, unknown>[]
  /** Declared-but-required columns no file header maps to. */
  missingRequired: string[]
  /** File headers that map to no declared column (ignored, not an error). */
  unmappedHeaders: string[]
  /** Declared optional columns absent from the file (sent as blank). */
  missingOptional: string[]
  warning?: string
}

/** Normalise a header or column name so "Employee ID" matches "employee_id". */
export function normalizeKey(value: string): string {
  return String(value ?? '')
    .replace(/^﻿/, '')          // strip a UTF-8 BOM off the first header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/** Strip the BOM but keep the header otherwise verbatim (it is the map key). */
function cleanHeader(value: string): string {
  return String(value ?? '').replace(/^﻿/, '').trim()
}

export async function parseCsvFile(file: File): Promise<ParsedFile> {
  const parsed = await parseFile(file)
  const headers = parsed.headers.map(cleanHeader)
  // Re-key the rows if cleaning changed a header (BOM on the first column).
  const changed = headers.some((h, i) => h !== parsed.headers[i])
  if (!changed) return { ...parsed, headers }
  const rows = parsed.rows.map((r) => {
    const out: Record<string, unknown> = {}
    parsed.headers.forEach((orig, i) => { out[headers[i]] = r[orig] })
    return out
  })
  return { filename: parsed.filename, headers, rows }
}

/** Positional sample cells — what `POST /genprocess/infer-file-columns` takes. */
export function sampleGrid(parsed: ParsedFile, limit = 200): unknown[][] {
  return parsed.rows.slice(0, limit).map((r) => parsed.headers.map((h) => r[h]))
}

/**
 * Apply a file source's mapping to a parsed file.
 *
 * Resolution per declared column, most explicit first:
 *   1. an explicit `mapping` entry (file header → column), then
 *   2. a header whose normalised form equals the column's (or its label's).
 * A column nothing resolves to is reported — required ones block the run,
 * optional ones simply arrive empty.
 */
export function applyMapping(parsed: ParsedFile, fileSource: FileSource | undefined): ImportResult {
  const columns: FileSourceColumn[] = fileSource?.columns || []
  const mapping = fileSource?.mapping || {}

  // column name → file header
  const headerFor = new Map<string, string>()
  for (const [header, column] of Object.entries(mapping)) {
    const h = parsed.headers.find((x) => normalizeKey(x) === normalizeKey(header))
    if (h && column) headerFor.set(String(column).toLowerCase(), h)
  }
  for (const col of columns) {
    const key = col.name.toLowerCase()
    if (headerFor.has(key)) continue
    const match = parsed.headers.find(
      (h) => normalizeKey(h) === normalizeKey(col.name)
        || (col.label ? normalizeKey(h) === normalizeKey(col.label) : false),
    )
    if (match) headerFor.set(key, match)
  }

  const used = new Set(headerFor.values())
  const missingRequired: string[] = []
  const missingOptional: string[] = []
  for (const col of columns) {
    if (headerFor.has(col.name.toLowerCase())) continue
    ;(col.required ? missingRequired : missingOptional).push(col.name)
  }

  const rows = parsed.rows.map((row) => {
    const out: Record<string, unknown> = {}
    for (const col of columns) {
      const header = headerFor.get(col.name.toLowerCase())
      out[col.name] = header ? row[header] : null
    }
    return out
  })

  let warning: string | undefined
  if (rows.length > IMPORT_WARN_ROWS) {
    warning = `${rows.length.toLocaleString()} rows — large imports are slow to `
      + 'review in the grid. Consider splitting the file.'
  }

  return {
    filename: parsed.filename,
    headers: parsed.headers,
    rows,
    missingRequired,
    missingOptional,
    unmappedHeaders: parsed.headers.filter((h) => !used.has(h)),
    warning,
  }
}

/** Human-readable reason the import cannot run, or ''. */
export function importBlocker(result: ImportResult | null): string {
  if (!result) return ''
  if (!result.rows.length) return 'The file has no data rows.'
  if (result.rows.length > IMPORT_MAX_ROWS) {
    return `${result.rows.length.toLocaleString()} rows exceeds the `
      + `${IMPORT_MAX_ROWS.toLocaleString()}-row import limit — split the file `
      + 'or load it through a database source instead.'
  }
  if (result.missingRequired.length) {
    return `The file has no column for required field(s): `
      + `${result.missingRequired.join(', ')}. Map them on the Source tab.`
  }
  return ''
}
